import { BaseApiClient, QueryParams } from "./base-api"
import {
  DeleteResponse,
  GenerationRequest,
  GenerationResponse
} from "@/schemas/api-responses"

const API_BASE_URL = import.meta.env.VITE_BASE_URL

// Image-specific interfaces
export interface UserImage {
  id: string
  user_email: string
  user_id: string
  image_url: string  // Public URL for the image
  file_name: string
  file_size: number
  title: string
  image_type: string
  status: string
  feedback?: string | null
  prompt: string
  model: string
  width: number
  height: number
  format: string
  aspect_ratio?: string  // Image aspect ratio (e.g., "1:1", "16:9", "3:4")
  created_at: string
  updated_at: string
  delete_request: boolean
  is_favourited?: boolean
  is_archived?: boolean
  archived_by?: string
  archived_at?: string
}

// These need to maintain original structure for compatibility with existing components
export interface ImageListResponse {
  success: boolean
  images: UserImage[]
  total_count: number
  total_unfiltered_count?: number
  limit: number
  offset: number
  has_more: boolean
}

export interface ImageResponse {
  success: boolean
  image: UserImage
}

// These can use shared types as they match the structure
export type DeleteImageResponse = DeleteResponse

// Extend shared generation interfaces for image-specific functionality
export interface ImageGenerationRequest extends GenerationRequest {
  width?: number
  height?: number
}

export type ImageGenerationResponse = GenerationResponse

export interface ImageStatusCountsResponse {
  approved: number
  pending: number
  rejected: number
  total: number
}

export interface FavouriteImageResponse {
  success: boolean
  is_favourited: boolean
  message: string
  image_id: string
  error?: string
}

export interface ArchiveImageResponse {
  success: boolean
  is_archived: boolean
  message: string
  image_id: string
  error?: string
}

export interface ImageListParams {
  limit?: number
  offset?: number
  status?: string
  user_email?: string
  sort_by?: string
  mode?: string
}

export interface User {
  email: string
  count: number
  role: string
}

export interface UsersResponse {
  users: User[]
}

// Remove the duplicate QueryParams type since we import it from base-api
// Remove duplicate helper functions since we use BaseApiClient methods

/**
 * Fetch user images with pagination
 * @param limit - Number of images to fetch (default: 20)
 * @param offset - Offset for pagination (default: 0)
 * @param mode - Optional mode filter
 * @param status - Optional status filter
 * @param sort_by - Optional sort parameter
 * @returns Promise<ImageListResponse>
 */
// Fetch all user images
export const fetchUserImages = async (
  limit = 20,
  offset = 0,
  mode?: string,
  status?: string,
  sort_by?: string
): Promise<ImageListResponse> => {
  const queryParams: QueryParams = {
    limit,
    offset
  }

  if (mode) {
    queryParams.mode = mode
  }

  if (status && status !== 'all') {
    queryParams.status = status
  }

  if (sort_by) {
    queryParams.sort_by = sort_by
  }

  return BaseApiClient.getRequest<ImageListResponse>(
    "/picasso/image/list",
    "Error fetching user images",
    queryParams
  )
}

/**
 * Delete an image by ID - supports both user and admin access
 * @param imageId - The ID of the image to delete
 * @returns Promise<DeleteImageResponse>
 */
export const deleteImage = async (imageId: string): Promise<DeleteImageResponse> => {
  return BaseApiClient.deleteRequest<DeleteImageResponse>(
    `/picasso/image/image/${imageId}`,
    "Error deleting image"
  )
}

/**
 * Request deletion of own image (content creator only)
 * @param imageId - Image ID to request deletion for
 * @returns Promise<{message: string, image_id: string, delete_request: boolean, updated_at: string}>
 */
export const requestImageDelete = async (imageId: string): Promise<{
  message: string;
  image_id: string;
  delete_request: boolean;
  updated_at: string;
}> => {
  return BaseApiClient.patchRequest<{
    message: string;
    image_id: string;
    delete_request: boolean;
    updated_at: string;
  }>(
    `/picasso/image/${imageId}/metadata`,
    { delete_request: true },
    "Error requesting image deletion"
  )
}

/**
 * Generate a new image
 * @param request - Image generation parameters
 * @returns Promise<ImageGenerationResponse>
 */
export const generateImage = async (request: ImageGenerationRequest): Promise<ImageGenerationResponse> => {
  return BaseApiClient.postRequest<ImageGenerationResponse>(
    "/picasso/image/generate",
    request,
    "generating image"
  )
}

/**
 * Upload an image with prompt and model
 * @param image - Image file to upload
 * @param prompt - Prompt text for the image
 * @param model - Model to use for processing
 * @returns Promise<ImageGenerationResponse>
 */
export const uploadImage = async (
  image: File,
  prompt: string,
  model: string
): Promise<ImageGenerationResponse> => {


  const headers = await BaseApiClient.getAuthHeaders();
  const formData = new FormData();
  formData.append('image', image);
  formData.append('prompt', prompt);
  formData.append('model', model);

  // Remove Content-Type for FormData - browser will set it with boundary
  const uploadHeaders = { ...headers };
  delete uploadHeaders['Content-Type'];

  const url = BaseApiClient.buildUrlWithParams(`${API_BASE_URL}/picasso/image/upload`, {});

  const response = await fetch(url, {
    method: 'POST',
    headers: uploadHeaders,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  return result;
}

/**
 * Update image metadata (admin function)
 * @param imageId - The ID of the image to update
 * @param status - New status for the image
 * @param feedback - Optional feedback text
 * @param delete_request - Optional delete request flag
 * @param title - Optional new title
 * @returns Promise<{message: string, image_id: string, status: string, feedback?: string, updated_at: string}>
 */
export const updateImageMetadata = async (
  imageId: string,
  status?: string,
  feedback?: string,
  delete_request?: boolean,
  title?: string
): Promise<{
  message: string;
  image_id: string;
  status?: string;
  feedback?: string;
  delete_request?: boolean;
  title?: string;
  updated_at: string;
}> => {
  const body: any = {}

  if (status !== undefined) body.status = status
  if (feedback !== undefined) body.feedback = feedback.trim() || undefined
  if (delete_request !== undefined) body.delete_request = delete_request
  if (title !== undefined) body.title = title.trim()

  return BaseApiClient.patchRequest<{
    message: string;
    image_id: string;
    status?: string;
    feedback?: string;
    delete_request?: boolean;
    title?: string;
    updated_at: string;
  }>(
    `/picasso/image/${imageId}/metadata`,
    body,
    "Error updating image metadata"
  )
}

/**
 * Fetch all users' images with pagination (admin function)
 * @param params - Query parameters for filtering and pagination
 * @returns Promise<ImageListResponse>
 */
export const fetchAllUsersImages = async (params: ImageListParams = {}): Promise<ImageListResponse> => {
  const queryParams: QueryParams = {
    limit: params.limit || 12,
    offset: params.offset || 0,
  }

  if (params.status && params.status !== "all") {
    queryParams.status = params.status
  }
  if (params.user_email && params.user_email !== "all") {
    queryParams.user_email = params.user_email
  }
  if (params.sort_by) {
    queryParams.sort_by = params.sort_by
  }

  return BaseApiClient.getRequest<ImageListResponse>(
    "/picasso/image/all",
    "Error fetching all users' images",
    queryParams
  )
}

/**
 * Fetch all users list
 * @returns Promise<UsersResponse>
 */
export const fetchImageUsers = async (): Promise<UsersResponse> => {
  return BaseApiClient.getRequest<UsersResponse>(
    "/picasso/image/users",
    "Error fetching image users"
  )
}

/**
 * Fetch image status counts (admin function)
 * @param userEmail - Optional user email filter
 * @returns Promise<ImageStatusCountsResponse>
 */
export const fetchImageStatusCounts = async (userEmail?: string): Promise<ImageStatusCountsResponse> => {
  const params: QueryParams = {}
  if (userEmail) {
    params.user_email = userEmail
  }

  return BaseApiClient.getRequest<ImageStatusCountsResponse>(
    "/picasso/image/status-counts",
    "Error fetching image status counts",
    params
  )
}

/**
 * Download image through backend (to handle CORS issues with signed URLs)
 * @param signedUrl - Signed URL of the image
 * @returns Promise<string> - Base64 encoded image
 */
export const getImageForDownload = async (signedUrl: string): Promise<string> => {
  const headers = await BaseApiClient.getAuthHeaders()
  const url = BaseApiClient.buildUrlWithParams(`${API_BASE_URL}/picasso/image/download`, { url: signedUrl })

  const response = await fetch(url, {
    method: "GET",
    headers,
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  // The backend returns a FileResponse (binary image), convert it to base64
  const arrayBuffer = await response.arrayBuffer()
  const bytes = new Uint8Array(arrayBuffer)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCodePoint(byte)
  }
  return btoa(binary)
}

/**
 * Toggle favourite status for an image
 * @param imageId - The image ID to toggle favourite
 * @returns Promise<FavouriteImageResponse>
 */
export const toggleImageFavourite = async (imageId: string): Promise<FavouriteImageResponse> => {
  return BaseApiClient.postRequest<FavouriteImageResponse>(
    `/picasso/image/favourite/${imageId}`,
    {},
    "Error toggling image favourite"
  )
}

/**
 * Get user's favourite images
 * @param limit - Maximum number of images to return
 * @param offset - Number of images to skip
 * @returns Promise<ImageListResponse>
 */
export const fetchFavouriteImages = async (
  limit: number = 100,
  offset: number = 0
): Promise<ImageListResponse> => {
  const params: QueryParams = { limit, offset }
  
  return BaseApiClient.getRequest<ImageListResponse>(
    "/picasso/image/favourites",
    "Error fetching favourite images",
    params
  )
}

/**
 * Toggle archive status for an image (admin only)
 * @param imageId - The image ID to toggle archive
 * @returns Promise<ArchiveImageResponse>
 */
export const toggleImageArchive = async (imageId: string): Promise<ArchiveImageResponse> => {
  return BaseApiClient.postRequest<ArchiveImageResponse>(
    `/picasso/image/archive/${imageId}`,
    {},
    "Error toggling image archive status"
  )
}

/**
 * Get archived images (admin only)
 * @param limit - Maximum number of images to return
 * @param offset - Number of images to skip
 * @returns Promise<ImageListResponse>
 */
export const fetchArchivedImages = async (
  limit: number = 100,
  offset: number = 0
): Promise<ImageListResponse> => {
  const params: QueryParams = { limit, offset }
  
  const response = await BaseApiClient.getRequest<ImageListResponse & { total?: number; total_count?: number }>(
    "/picasso/image/archives",
    "Error fetching archived images",
    params
  )
  
  return {
    ...response,
    total_count: response.total_count ?? response.total ?? 0
  }
}
